import { Inject, Injectable, OnDestroy } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, UserClaims } from '@okta/okta-auth-js';
import { from, Observable, ReplaySubject, Subject } from 'rxjs';
import { distinctUntilChanged, switchMap, take, takeUntil } from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { firstValueOrDefault } from 'src/util/rxjs';
import { createSafeProps } from '../tcs-app-insights/app-insights-util';
import { ImpersonationService } from './impersonation.service';

type TcsUserClaims = UserClaims & { EmployeeNumber?: string };

function decode(token: string): TcsUserClaims {
  const claims = JSON.parse(atob(token.split('.')[1]));
  return claims;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService implements OnDestroy {
  private _accessToken$ = new ReplaySubject<string | undefined>(1);
  private _idToken$ = new ReplaySubject<string | undefined>(1);
  private _accessTokenClaims?: TcsUserClaims;
  private _onDestroy$ = new Subject();

  public get accessClaims(): TcsUserClaims | undefined {
    return this._accessTokenClaims;
  }

  private _accessClaims$ = new ReplaySubject<TcsUserClaims | undefined>(1);
  public get accessClaims$(): Observable<TcsUserClaims | undefined> {
    return this._accessClaims$.asObservable();
  }

  private _idClaims?: TcsUserClaims;
  public get idClaims(): TcsUserClaims | undefined {
    return this._idClaims;
  }

  private _idClaims$ = new ReplaySubject<TcsUserClaims | undefined>(1);
  public get idClaims$(): Observable<TcsUserClaims | undefined> {
    return this._idClaims$.asObservable();
  }

  public isAuthenticated(): Promise<boolean> {
    return this._okta.isAuthenticated();
  }

  public get isAuthenticated$(): Observable<boolean> {
    return this._accessClaims$.pipe(
      switchMap((claims) => from(this._okta.isAuthenticated())),
      distinctUntilChanged(),
    );
  }

  get accessToken$(): Observable<string | undefined> {
    return this._accessToken$.asObservable();
  }

  public getAccessToken(): Promise<string | undefined> {
    return firstValueOrDefault(this._accessToken$.pipe(take(1)), undefined);
  }

  public getIdToken(): Promise<string | undefined> {
    return firstValueOrDefault(this._idToken$.pipe(take(1)), undefined);
  }

  public async tryRenewToken(): Promise<boolean> {
    const currentToken = await this.getAccessToken();
    const maybeRenewedToken = await this._getOrRenewAccessToken();
    return maybeRenewedToken != null && currentToken !== maybeRenewedToken;
  }

  private _getOrRenewAccessToken(): Promise<string | null> {
    return this._okta.getOrRenewAccessToken();
  }

  constructor(
    @Inject(OKTA_AUTH) private _okta: OktaAuth,
    private _oktaState: OktaAuthStateService,
    private _impersonation: ImpersonationService,
    private _log: Logger,
  ) {
    this._accessToken$
      .pipe(distinctUntilChanged(), takeUntil(this._onDestroy$))
      .subscribe((accessToken) => {
        this._accessTokenClaims = accessToken != null ? decode(accessToken) : undefined;
        this._accessClaims$.next(this._accessTokenClaims);
      });

    this._idToken$
      .pipe(distinctUntilChanged(), takeUntil(this._onDestroy$))
      .subscribe((idToken) => {
        this._idClaims = idToken != null ? decode(idToken) : undefined;
        this._idClaims$.next(this._idClaims);
      });

    // Update tokens when changed
    // if the browser allows third-party cookies
    //  - this executes on subscribe and after tokens are created, renewed, or expired
    //  - on renewed the tokens have been updated
    //  - on expired the session length has elapsed and the user is required to login again
    // if the browser blocks third-party cookies
    //  - this executes on subscribe and after tokens are created or expired
    //  - on expired causes a redirect to okta. If the session length has elapsed a login is required,
    //    otherwise new tokens are returned
    this._oktaState.authState$.pipe(takeUntil(this._onDestroy$)).subscribe((authState) => {
      this._accessToken$.next(authState.accessToken?.accessToken);
      this._idToken$.next(authState.idToken?.idToken);
    });
  }

  public async init() {
    const idToken = this._okta.getIdToken();
    if (idToken != null) {
      const claims = decode(idToken);
      const config = this._okta.options;
      // Clear the local/session storage if the current tokens we have don't match the issuer (Okta tenant)
      // we're configured to use. This happens during local development and alternating between training/prod
      // and lower environments. Otherwise, the app will just throw an uncatchable exception and the developer has to clear
      // the local storage manually to get around this
      if (claims.iss !== config.issuer) {
        const props = this._log.info(
          'Claims/config issuer mismatch detected, resetting storage',
          createSafeProps({
            claims,
            config,
          }),
        );
        this.clearLogin();
        await this.login({
          caller: 'AuthenticationService.init',
          fromUri: window.location.href,
        });
      }
    }
  }

  public async login({ fromUri, caller }: { fromUri?: string; caller: string }): Promise<void> {
    fromUri = fromUri?.toLowerCase().includes('callback') ?? false ? undefined : fromUri;
    this._log.info('AuthenticationService.login', createSafeProps({ fromUri, caller }));
    return this._okta.signInWithRedirect({ originalUri: fromUri });
  }

  public async logout({ url, caller }: { url?: string; caller: string }) {
    this._log.info('AuthenticationService.logout', createSafeProps({ url, caller }));
    this.clearLogin();
    url = url?.toLowerCase().includes('callback') ?? false ? undefined : url;
    await this._okta.signOut({ postLogoutRedirectUri: url ?? '/' });
  }

  private async clearLogin() {
    this._impersonation.clearImpersonation();
    localStorage.removeItem('okta-cache-storage');
    localStorage.removeItem('okta-token-storage');
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
